import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { lastValueFrom, map } from 'rxjs';
import {
  authenticationControllerDeleteProfilbildV1,
  authenticationControllerGetMyUserDetailsV1,
  authenticationControllerGetProfilbildLimitsV1,
  authenticationControllerGetSpracheOptionsV1,
  authenticationControllerUpdateProfileV1,
  authenticationControllerUploadProfilbildV1,
  LexplusUpdateProfileDto,
} from '@lexplus-authentication-api';
import { API_CONFIGURATION } from '@lexplus-environments';
import { ProfileState } from '../../../states/profile/profile-state';
import { ProfileStateSetProfile } from '../../../states/profile/profile.actions';

/**
 * Everything «Mein Profil» asks of the authentication API, which acts on the signed-in user in
 * either app. It also keeps the profile of the session in step with what was written, because the
 * account menu shows the name and the picture from there.
 */
@Injectable({ providedIn: 'root' })
export class MeinProfilService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly store = inject(Store);

  /** The signed-in user's own record, which needs no administrator, unlike a lookup by id. */
  public readMyDetails() {
    return authenticationControllerGetMyUserDetailsV1(this.httpClient, this.apiConfiguration.baseUrlAuth).pipe(map((r) => r.body));
  }

  public readSpracheOptions() {
    return authenticationControllerGetSpracheOptionsV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {}).pipe(map((r) => r.body));
  }

  public readProfilbildLimits() {
    return authenticationControllerGetProfilbildLimitsV1(this.httpClient, this.apiConfiguration.baseUrlAuth).pipe(map((r) => r.body));
  }

  /** The absolute URL of a picture path the API reported. The browser fetches the `<img>` itself, so the session cookie rides along. */
  public buildProfilbildUrl(path: string | null): string | null {
    return path ? `${this.apiConfiguration.baseUrlAuth}${path}` : null;
  }

  /** Writes the profile and puts the response back into the state. */
  public async updateProfile(body: LexplusUpdateProfileDto): Promise<void> {
    const response = await lastValueFrom(
      authenticationControllerUpdateProfileV1(this.httpClient, this.apiConfiguration.baseUrlAuth, { body }),
    );
    this.store.dispatch(new ProfileStateSetProfile(response.body));
  }

  /** Uploads a picture; resolves to the path it can now be read from. */
  public async uploadProfilbild(file: File): Promise<string | null> {
    const response = await lastValueFrom(
      authenticationControllerUploadProfilbildV1(this.httpClient, this.apiConfiguration.baseUrlAuth, { body: { file } }),
    );
    this.setProfilbildPath(response.body.profilbildUrl);
    return response.body.profilbildUrl;
  }

  public async deleteProfilbild(): Promise<void> {
    await lastValueFrom(authenticationControllerDeleteProfilbildV1(this.httpClient, this.apiConfiguration.baseUrlAuth));
    this.setProfilbildPath(null);
  }

  private setProfilbildPath(path: string | null): void {
    const profile = this.store.selectSnapshot(ProfileState.profile);
    if (profile) {
      this.store.dispatch(new ProfileStateSetProfile({ ...profile, profilbildUrl: path }));
    }
  }
}
